/**
 * Роли: подписи и ступени — один источник на весь интерфейс.
 *
 * Список выписывался уже в трёх местах: форма создания, таблица сотрудников,
 * переключатели доступа. С четырьмя ролями это сходило с рук; с восемью
 * расхождение между копиями стало вопросом времени — и заметили бы его не
 * сразу, потому что роль просто не появлялась бы в одном из списков.
 */

import {
  ROLE_TIER,
  ROLE_TRACK,
  type RoleTier,
  type RoleTrack,
} from "@tw/permissions";

export const ROLE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "MANAGER_INTERN", label: "Стажёр менеджера" },
  { value: "MANAGER", label: "Менеджер" },
  { value: "SENIOR_MANAGER", label: "Старший менеджер" },
  { value: "OPERATOR_INTERN", label: "Стажёр оператора" },
  { value: "OPERATOR", label: "Оператор" },
  { value: "SENIOR_OPERATOR", label: "Старший оператор" },
  { value: "HR", label: "HR" },
  { value: "ADMIN", label: "Администратор" },
];

export const ROLE_LABELS: Record<string, string> = Object.fromEntries(
  ROLE_OPTIONS.map((r) => [r.value, r.label]),
);

/**
 * Ступень роли.
 *
 * Читается защищённо: роль приходит с сервера строкой, и версия API может
 * знать роль, о которой этот экран ещё не слышал. Неизвестная роль не должна
 * ронять таблицу — она просто не попадёт ни в один из списков.
 */
export function tierOf(role: string): RoleTier | null {
  return (ROLE_TIER as Record<string, RoleTier>)[role] ?? null;
}

/**
 * Цвет ветки роли.
 *
 * Ролей восемь, и глазом они не различаются: «Стажёр оператора» и «Стажёр
 * менеджера» отличаются одним словом в середине. При этом ветка — главное, что
 * о сотруднике нужно понять с первого взгляда: она определяет, с какими
 * аккаунтами он работает.
 *
 * Цветов три, по числу веток, а не восемь по числу ролей: ступень внутри ветки
 * читается словом и в цвете не нуждается, а восемь оттенков в одной таблице
 * превратились бы в мешанину.
 *
 * Администратор вне веток и намеренно нейтрален: выделять его цветом значило
 * бы намекать, что это ещё одна ветка, тогда как он стоит над ними.
 */
const TRACK_TONES: Record<RoleTrack, string> = {
  MANAGER: "var(--accent-fill)",
  OPERATOR: "var(--ok)",
  HR: "var(--warn)",
  ADMIN: "var(--muted)",
};

/**
 * Цвет по роли. Неизвестная роль — нейтральный: клиент и сервер обновляются
 * не одновременно, и роль, о которой этот экран ещё не слышал, не должна
 * выглядеть ошибкой.
 */
export function roleTone(role: string): string {
  const track = (ROLE_TRACK as Record<string, RoleTrack | undefined>)[role];
  if (!track) return "var(--muted)";
  return TRACK_TONES[track] ?? "var(--muted)";
}
